import { api } from '../shared/services/api';
import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner, PageFooter } from '../shared/components';
import { infoIcon } from '../shared/components/InfoIcon';
import { icon } from '../shared/components/icons';
import { paginate, filterBar, pager } from '../shared/lib/tableControls';
import Chart from 'chart.js/auto';

interface ProjectionRow {
  id: number | null;
  month: number;
  listing_id?: number;
  projected_gross: number;
  projected_mgmt_fee: number;
  projected_net: number;
  actual_gross: number;
  actual_mgmt_fee: number;
  actual_net: number;
}

interface BLREstimate {
  id: number;
  fee_name: string;
  price_per_occurrence: number;
  take_up_rate: number;
}

export class ProjectionsPage {
  private container: HTMLElement;
  private projectionsData: ProjectionRow[] = [];
  private blrProjections: BLREstimate[] = [];
  private currentYear: number = 2026;
  private viewType: string = 'portfolio';
  private chartInstance: Chart | null = null;
  private expenseRatio: number = 72;
  // Which metric the cards/chart are showing (gor | cr | blr | mor | tor).
  private selectedMetric: 'gor' | 'cr' | 'blr' | 'mor' | 'tor' = 'gor';
  // Metric registry overrides (rename GOR etc. in Configuration) — key → entry.
  private metricRegistry: Record<string, { display_name?: string | null; formula_text?: string | null; notes?: string | null }> = {};
  private registryLoaded = false;

  private async ensureRegistry(): Promise<void> {
    if (this.registryLoaded) return;
    try {
      const r = await fetch('/api/metric-registry');
      if (r.ok) this.metricRegistry = await r.json();
    } catch (_) { /* optional */ }
    this.registryLoaded = true;
  }
  private regName(key: string, fallback: string): string {
    return this.metricRegistry[key]?.display_name || fallback;
  }
  private airroiScenario: string = 'off';
  private airroiCompareData: any = null;
  private airroiMonthlyData: any = null;
  private airroiMonth: string = 'all';
  private airroiCharts: Chart[] = [];

  // AirROI compare per-property table — client-side filter + pagination state.
  private cmpSearch: string = '';
  private cmpCapture: string = 'all'; // all | under50 | 50to80 | over80
  private cmpPage: number = 1;
  private readonly cmpPageSize: number = 25;
  private cmpSortKey: string = 'behind';   // default: biggest money left on the table first
  private cmpSortDir: 'asc' | 'desc' = 'desc';
  private cmpBasis: 'low' | 'expected' | 'high' = 'expected'; // follows the AirROI scenario

  constructor(container: HTMLElement) {
    this.container = container;
  }

  // Guarded money string — never renders $NaN.
  private static money(n: number | null | undefined): string {
    return Number.isFinite(n as number) ? `$${Formatters.formatNumber(n)}` : '—';
  }
  // Guarded percent string — never renders NaN%.
  private static pct(n: number | null | undefined, decimals = 1): string {
    return Number.isFinite(n as number) ? `${(n as number).toFixed(decimals)}%` : '—';
  }

  async render(): Promise<void> {
    await this.loadSettings();
    await this.ensureRegistry();
    this.container.innerHTML = `
      <style>
        /* Projections page — overflow-safe stat grids */
        #proj-stats.stats-grid { grid-template-columns: repeat(6, minmax(0, 1fr)) !important; }
        #proj-airroi-kpis.stats-grid,
        #proj-airroi-compare-container .stats-grid { grid-template-columns: repeat(5, minmax(0, 1fr)) !important; }
        @media (max-width: 1024px) {
          #proj-stats.stats-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
          #proj-airroi-kpis.stats-grid { grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
        }
        @media (max-width: 640px) {
          #proj-stats.stats-grid,
          #proj-airroi-kpis.stats-grid { grid-template-columns: repeat(2, minmax(0, 1fr)) !important; }
        }
      </style>
      <div class="page-content">
        <div class="dash-head">
          <h1>Revenue Projections</h1>
          <p>Where each property and the portfolio are projected to land vs. goal — a gap is the revenue-management opportunity.</p>
        </div>
        <!-- Filters -->
        <div class="filters">
          <div class="filter-group">
            <label>Year</label>
            <select id="proj-year">
              <option value="2024">2024</option>
              <option value="2025">2025</option>
              <option value="2026" selected>2026</option>
              <option value="2027">2027</option>
            </select>
          </div>
          <div class="filter-group">
            <label>View</label>
            <select id="proj-view-type">
              <option value="portfolio" selected>Portfolio Rollup</option>
              <option value="property">Single Property</option>
            </select>
          </div>
          <div class="filter-group" id="proj-property-group" style="display: none;">
            <label>Property</label>
            <select id="proj-property"></select>
          </div>
          <div class="filter-group" id="proj-expense-group" style="display: none;">
            <label>Target Expense Ratio</label>
            <div style="display: flex; align-items: center; gap: 4px;">
              <input type="number" id="proj-expense-ratio" value="${this.expenseRatio}" min="0" max="100" style="width: 60px;">
              <span>%</span>
            </div>
          </div>
          <div class="filter-group">
            <label>Chart Period</label>
            <select id="proj-chart-month">
              <option value="all" selected>Full Year</option>
              <option value="h1">H1 (Jan - Jun)</option>
              <option value="h2">H2 (Jul - Dec)</option>
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>
          </div>
          <div class="filter-group">
            <label>Show Metrics</label>
            <div style="display: flex; gap: 12px; height: 36px; align-items: center;">
              <label style="display: flex; align-items: center; gap: 4px; font-size: 13px; text-transform: none; letter-spacing: 0; font-weight: 400; color: var(--text);">
                <input type="checkbox" id="proj-show-projected" checked> Projected
              </label>
              <label style="display: flex; align-items: center; gap: 4px; font-size: 13px; text-transform: none; letter-spacing: 0; font-weight: 400; color: var(--text);">
                <input type="checkbox" id="proj-show-actual" checked> Actual
              </label>
            </div>
          </div>
          <div class="filter-group" id="proj-airroi-scenario-group">
            <label>AirROI Scenario</label>
            <select id="proj-airroi-scenario">
              <option value="off" selected>Off (stored)</option>
              <option value="conservative">Conservative (p25)</option>
              <option value="expected">Expected (avg)</option>
              <option value="optimistic">Optimistic (p90)</option>
            </select>
          </div>
        </div>
        <!-- Data actions — own toolbar so they stay a tidy, consistent cluster at any width -->
        <div class="proj-actions">
          <button id="proj-generate-btn" class="btn btn-secondary btn-sm">Generate from PriceLabs</button>
          <button id="proj-generate-airroi-btn" class="btn btn-secondary btn-sm">Generate from AirROI</button>
          <button id="proj-sync-btn" class="btn btn-secondary btn-sm">Sync Actuals</button>
        </div>
        <div id="proj-airroi-note" style="font-size: 12px; color: var(--info-text); margin: -4px 0 8px;"></div>

        <!-- Stats Grid -->
        <div class="kpi-grid" id="proj-stats">
          ${LoadingSpinner.render()}
        </div>

        <!-- Chart -->
        <div class="table-container">
          <div class="table-header">
            <h3>Monthly Projected vs Actual ${infoIcon('projection')}</h3>
            <div style="font-size: 12px;">
              <span style="display: inline-block; width: 12px; height: 12px; background: #3b82f6; border-radius: 2px; margin-right: 4px;"></span> Projected
              <span style="display: inline-block; width: 12px; height: 12px; background: #22c55e; border-radius: 2px; margin-left: 12px; margin-right: 4px;"></span> Actual
            </div>
          </div>
          <div id="proj-chart-container" style="padding: 20px; min-height: 300px;">
            ${LoadingSpinner.render()}
          </div>
        </div>

        <!-- AirROI charts -->
        <div class="table-container" id="proj-airroi-charts-container" style="display: none;">
          <div class="table-header">
            <h3>AirROI Market Data</h3>
            <div style="display: flex; align-items: center; gap: 6px; font-size: 12px;">
              <label for="proj-airroi-month" style="color: #64748b;">Month</label>
              <select id="proj-airroi-month">
                <option value="all" selected>Full Year</option>
                <option value="1">January</option>
                <option value="2">February</option>
                <option value="3">March</option>
                <option value="4">April</option>
                <option value="5">May</option>
                <option value="6">June</option>
                <option value="7">July</option>
                <option value="8">August</option>
                <option value="9">September</option>
                <option value="10">October</option>
                <option value="11">November</option>
                <option value="12">December</option>
              </select>
            </div>
          </div>
          <div id="proj-airroi-month-summary" style="padding: 12px 16px 0; font-size: 12px; color: #64748b;"></div>
          <div style="padding: 16px; display: grid; gap: 24px;">
            <div>
              <div style="font-size: 13px; color: #64748b; margin-bottom: 8px;">Booking pace by month — <span style="color:#16a34a;">realized</span> + <span style="color:#0ea5e9;">upcoming</span> booked vs. AirROI projected (dashed)</div>
              <div style="height: 260px;"><canvas id="airroi-season-chart"></canvas></div>
            </div>
            <!-- The top-20 potential and top-15 gap bar charts were replaced by
                 the sortable per-property leakage table below — 20 unlabeled
                 bars showed less than one sortable "Behind $" column does. -->
          </div>
        </div>

        <!-- AirROI Market Potential vs Booked (per property) -->
        <div class="table-container" id="proj-airroi-compare-container" style="display: none;">
          <div class="table-header">
            <h3>AirROI Market Potential vs Booked — by Property</h3>
            <div id="proj-airroi-compare-summary" style="font-size: 12px; color: #64748b;"></div>
          </div>
          <div class="stats-grid" id="proj-airroi-kpis" style="padding: 12px 16px 0;"></div>
          <div style="padding: 8px 16px; font-size: 12px; color: #64748b;">
            <strong>Booked = full-year 2026, on the books</strong> (annual, not month-by-month) vs. AirROI's
            full-year market potential. <span style="color:#16a34a;">Realized</span> = stays already completed ·
            <span style="color:#0ea5e9;">Upcoming</span> = future reservations already confirmed ·
            <strong>Booked</strong> = Realized + Upcoming. Capture % = Booked ÷ Market Expected.
          </div>
          <div id="proj-airroi-compare-controls" style="padding: 8px 16px 0;"></div>
          <div class="table-scroll">
            <table style="min-width: 920px;">
              <thead>
                <tr id="proj-airroi-compare-head">
                  <th data-cmp-sort="name" style="cursor:pointer;">Property ↕</th>
                  <th data-cmp-sort="market_expected" style="text-align: right; cursor:pointer;">Market Expected ↕ ${infoIcon('goal')}</th>
                  <th style="text-align: right;">Market Range (p25–p90)</th>
                  <th data-cmp-sort="realized" style="text-align: right; color:#16a34a; cursor:pointer;">Realized ↕</th>
                  <th data-cmp-sort="upcoming" style="text-align: right; color:#0ea5e9; cursor:pointer;">Upcoming ↕</th>
                  <th data-cmp-sort="booked" style="text-align: right; cursor:pointer;">Booked ↕</th>
                  <th data-cmp-sort="behind" style="text-align: right; color:#ea580c; cursor:pointer;">Behind $ ↕</th>
                  <th data-cmp-sort="capture_pct" style="text-align: right; cursor:pointer;">Capture % ↕ ${infoIcon('pace_vs_goal')}</th>
                </tr>
              </thead>
              <tbody id="proj-airroi-compare-body"></tbody>
            </table>
          </div>
          <div id="proj-airroi-compare-pager" style="padding: 0 16px 12px;"></div>
        </div>

        <!-- Revenue by Fee Type -->
        <div class="table-container">
          <div class="table-header">
            <h3>Revenue by Fee Type</h3>
          </div>
          <div class="table-scroll">
            <table style="min-width: 500px;">
              <thead>
                <tr><th>Fee Type</th><th>Category</th><th>Total Amount</th><th>Your Revenue</th><th>Treatment</th></tr>
              </thead>
              <tbody id="proj-fee-breakdown-body">
                <tr><td colspan="5">${LoadingSpinner.render()}<\/td></tr>
              </tbody>
              <tfoot id="proj-fee-breakdown-foot"></tfoot>
            </table>
          </div>
        </div>

        <!-- BLR Projections -->
        <div class="table-container">
          <div class="table-header">
            <h3>BLR Projections (Bottom Line Revenue)</h3>
            <button id="add-blr-btn" class="btn btn-sm btn-primary">+ Add BLR Fee</button>
          </div>
          <div class="table-scroll">
            <table style="min-width: 500px;">
              <thead>
                <tr><th>Fee Name</th><th>Price/Occurrence</th><th>Take-Up Rate %</th><th>Actions</th></tr>
              </thead>
              <tbody id="blr-projections-body">
                <tr><td colspan="4">${LoadingSpinner.render()}<\/td><\/tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Monthly Data Table -->
        <div class="table-container">
          <div class="table-header">
            <h3>Monthly Data Entry</h3>
            <button id="proj-save-btn" class="btn btn-primary btn-sm">Save All Changes</button>
          </div>
          <div class="table-scroll">
            <table style="min-width: 800px;">
              <thead>
                <tr>
                  <th>Month</th>
                  <th style="background: #eff6ff;">Proj Gross</th>
                  <th style="background: #eff6ff;">Proj Mgmt Fee</th>
                  <th style="background: #eff6ff;">Proj Net</th>
                  <th style="background: #f0fdf4;">Actual Gross</th>
                  <th style="background: #f0fdf4;">Actual Mgmt Fee</th>
                  <th style="background: #f0fdf4;">Actual Net</th>
                  <th>Gross Var</th>
                </tr>
              </thead>
              <tbody id="proj-table-body">
                <tr><td colspan="8">${LoadingSpinner.render()}<\/td></tr>
              </tbody>
              <tfoot id="proj-table-foot"></tfoot>
            </table>
          </div>
        </div>
        ${PageFooter.render([
          { abbr: 'GOR',           full: 'Gross Owner Revenue',      note: 'total revenue collected from guests' },
          { abbr: 'CR',            full: 'Commission Revenue',       note: 'management commission — your percentage of GOR' },
          { abbr: 'BLR',          full: 'Bottom Line Revenue',      note: 'additional income from ancillary fees and upsells' },
          { abbr: 'MOR',           full: 'Management Owner Revenue', note: 'total management income — CR + BLR' },
          { abbr: 'TOR',           full: 'Total Owner Revenue',      note: 'estimated net return to the property owner after expenses' },
          { abbr: 'Proj Gross',    full: 'Projected Gross Revenue',  note: 'forecast total guest revenue for the period' },
          { abbr: 'Proj Mgmt Fee', full: 'Projected Management Fee', note: 'forecast commission to be earned' },
          { abbr: 'Proj Net',      full: 'Projected Net Revenue',    note: 'projected gross minus projected expenses' },
          { abbr: 'Gross Var',     full: 'Gross Variance',           note: 'actual gross minus projected gross — positive means you beat forecast' },
          { abbr: 'H1',            full: 'First Half',               note: 'January through June' },
          { abbr: 'H2',            full: 'Second Half',              note: 'July through December' },
          { abbr: 'Take-Up Rate',  full: 'Take-Up Rate',             note: 'percentage of reservations expected to purchase this ancillary service' },
          { abbr: 'Treatment',     full: 'Fee Treatment',            note: 'Commission = counted in CR; Pass-Through = flows to the owner' },
        ])}
      </div>
    `;

    await this.loadData();
    this.bindEvents();
  }

  private async loadSettings(): Promise<void> {
    try {
      const settings = await api.get<{ default_expense_ratio_percent: number }>('/settings');
      if (settings) {
        this.expenseRatio = settings.default_expense_ratio_percent || 72;
      }
    } catch (e) {
      console.error('Failed to load settings', e);
    }
  }

  private async loadData(): Promise<void> {
    this.currentYear = parseInt((document.getElementById('proj-year') as HTMLSelectElement).value);
    this.viewType = (document.getElementById('proj-view-type') as HTMLSelectElement).value;

    await this.loadProjections();
    await this.applyAirroiScenario();
    await this.loadAirroiCompare();
    await this.renderAirroiCharts();
    await this.loadBLRProjections();
    await this.loadFeeBreakdown();
    this.renderChart();
    this.renderProjectionsTable();
    this.renderStats();
  }

  private async loadProjections(): Promise<void> {
    try {
      const url = this.viewType === 'portfolio' 
        ? `/projections/summary/${this.currentYear}`
        : `/projections?year=${this.currentYear}`;
      
      const data = await api.get<any>(url);
      if (data) {
        if (this.viewType === 'portfolio' && data.monthly) {
          this.projectionsData = data.monthly.map((row: any) => ({
            id: row.id,
            month: parseInt(row.month),
            projected_gross: row.projected_gross || 0,
            projected_mgmt_fee: row.projected_mgmt_fee || 0,
            projected_net: row.projected_net || 0,
            actual_gross: row.actual_gross || 0,
            actual_mgmt_fee: row.actual_mgmt_fee || 0,
            actual_net: row.actual_net || 0
          }));
        } else if (Array.isArray(data)) {
          this.projectionsData = data;
        }
      }
      
      // Ensure all 12 months exist
      for (let i = 1; i <= 12; i++) {
        if (!this.projectionsData.find(p => p.month === i)) {
          this.projectionsData.push({
            id: null,
            month: i,
            projected_gross: 0,
            projected_mgmt_fee: 0,
            projected_net: 0,
            actual_gross: 0,
            actual_mgmt_fee: 0,
            actual_net: 0
          });
        }
      }
      this.projectionsData.sort((a, b) => a.month - b.month);
    } catch (error) {
      console.error('Failed to load projections:', error);
    }
  }

  // Overlay an AirROI scenario (conservative/expected/optimistic) onto the projected line.
  // Portfolio view only — the cached AirROI projection is a portfolio rollup.
  private async applyAirroiScenario(): Promise<void> {
    const note = document.getElementById('proj-airroi-note');
    if (this.airroiScenario === 'off' || this.viewType !== 'portfolio') {
      if (note) note.textContent = '';
      return;
    }
    try {
      const data = await api.get<any>(`/projections/airroi/${this.currentYear}?scenario=${this.airroiScenario}`);
      if (!data || !data.available) {
        if (note) note.textContent = 'No AirROI projection yet for this year — click "Generate from AirROI".';
        return;
      }
      const byMonth = new Map<number, any>(data.monthly.map((m: any) => [m.month, m]));
      this.projectionsData.forEach(row => {
        const m = byMonth.get(row.month);
        if (m) {
          row.projected_gross = m.projected_gross;
          row.projected_mgmt_fee = m.projected_mgmt_fee;
          row.projected_net = m.projected_net;
        }
      });
      if (note) {
        const a = data.annual;
        note.innerHTML = `AirROI market projection (${data.listings_count} listings) — annual range: ` +
          `<strong>$${Formatters.formatNumber(a.low)}</strong> conservative · ` +
          `<strong>$${Formatters.formatNumber(a.expected)}</strong> expected · ` +
          `<strong>$${Formatters.formatNumber(a.high)}</strong> optimistic`;
      }
    } catch (e) {
      console.error('Failed to apply AirROI scenario:', e);
    }
  }

  // Per-property table: AirROI market potential vs actual booked revenue for the year.
  private async loadAirroiCompare(): Promise<void> {
    const container = document.getElementById('proj-airroi-compare-container');
    const body = document.getElementById('proj-airroi-compare-body');
    const summary = document.getElementById('proj-airroi-compare-summary');
    if (!container || !body) return;

    if (this.viewType !== 'portfolio') { container.style.display = 'none'; return; }

    try {
      const data = await api.get<any>(`/projections/airroi/compare/${this.currentYear}`);
      this.airroiCompareData = data;
      if (!data || !data.available || !data.rows?.length) { container.style.display = 'none'; return; }

      container.style.display = '';
      const t = data.totals;
      const captureTot = t.market_expected > 0 ? Math.round((t.booked / t.market_expected) * 100) : 0;
      const kpis = document.getElementById('proj-airroi-kpis');
      if (kpis) {
        kpis.innerHTML = `
          <div class="stat-card"><div class="stat-label">Market Potential</div><div class="stat-value">${ProjectionsPage.money(t.market_expected)}</div></div>
          <div class="stat-card"><div class="stat-label" style="color:var(--success);">Realized</div><div class="stat-value">${ProjectionsPage.money(t.realized || 0)}</div></div>
          <div class="stat-card"><div class="stat-label" style="color:var(--info);">Upcoming</div><div class="stat-value">${ProjectionsPage.money(t.upcoming || 0)}</div></div>
          <div class="stat-card highlight"><div class="stat-label">Booked</div><div class="stat-value">${ProjectionsPage.money(t.booked)}</div></div>
          <div class="stat-card"><div class="stat-label">Capture %</div><div class="stat-value">${ProjectionsPage.pct(captureTot, 0)}</div></div>
        `;
      }
      if (summary) {
        const capture = captureTot;
        summary.textContent =
          `Full-year 2026: booked $${Formatters.formatNumber(t.booked)} of $${Formatters.formatNumber(t.market_expected)} market potential (${capture}%)` +
          ` — realized $${Formatters.formatNumber(t.realized || 0)}, upcoming $${Formatters.formatNumber(t.upcoming || 0)}` +
          ` · sorted by capture %, lowest first`;
      }

      // Reset to first page on a fresh data load; the table itself is filtered
      // + paginated client-side over the already-fetched rows.
      this.cmpPage = 1;
      this.bindCompareSort();
      this.renderCompareTable();
    } catch (e) {
      console.error('Failed to load AirROI comparison:', e);
      container.style.display = 'none';
    }
  }

  // Filter + paginate the already-fetched AirROI compare rows. Re-renders on
  // search / capture-filter / page change without touching the network.
  private renderCompareTable(): void {
    const body = document.getElementById('proj-airroi-compare-body');
    const controls = document.getElementById('proj-airroi-compare-controls');
    const pagerEl = document.getElementById('proj-airroi-compare-pager');
    if (!body) return;

    // Expected basis follows the AirROI scenario dropdown — low (p25) /
    // stored expected (p50) / high (p90) — so switching the comparison
    // percentile re-bases Behind $ and Capture % immediately.
    const basisOf = (r: any) => {
      if (this.cmpBasis === 'low' && r.market_low != null) return r.market_low;
      if (this.cmpBasis === 'high' && r.market_high != null) return r.market_high;
      return r.market_expected;
    };
    const allRows: any[] = (this.airroiCompareData?.rows || []).map((r: any) => {
      const basis = basisOf(r);
      const booked = r.booked || 0;
      return {
        ...r,
        basis,
        behind: Math.max(0, Math.round(basis - booked)),
        capture_pct: basis > 0 ? Math.round((booked / basis) * 100) : null,
      };
    });
    const search = this.cmpSearch.trim().toLowerCase();
    const filtered = allRows.filter((r: any) => {
      if (search && !String(r.name || '').toLowerCase().includes(search)) return false;
      const pct = r.capture_pct;
      if (this.cmpCapture === 'under50') return pct != null && pct < 50;
      if (this.cmpCapture === '50to80') return pct != null && pct >= 50 && pct < 80;
      if (this.cmpCapture === 'over80')  return pct != null && pct >= 80;
      return true;
    });

    // Sortable columns; default = biggest Behind $ first (where the money is).
    const dir = this.cmpSortDir === 'asc' ? 1 : -1;
    filtered.sort((a: any, b: any) => {
      const k = this.cmpSortKey;
      if (k === 'name') return dir * String(a.name || '').localeCompare(String(b.name || ''));
      return dir * ((Number(a[k]) || 0) - (Number(b[k]) || 0));
    });

    const pg = paginate(filtered, this.cmpPage, this.cmpPageSize);
    this.cmpPage = pg.page;

    if (controls) {
      controls.innerHTML = filterBar({
        searchId: 'proj-cmp-search',
        searchValue: this.cmpSearch,
        searchPlaceholder: 'Search properties…',
        selects: [{
          id: 'proj-cmp-capture',
          value: this.cmpCapture,
          options: [
            { value: 'all',     label: 'All capture' },
            { value: 'under50', label: 'Under 50%' },
            { value: '50to80',  label: '50–80%' },
            { value: 'over80',  label: '80%+' },
          ],
        }],
        countLabel: `${pg.total} propert${pg.total === 1 ? 'y' : 'ies'}`,
      });
    }

    if (!pg.total) {
      body.innerHTML = '<tr><td colspan="7" class="empty-state" style="padding:24px;">No properties match the current filters.</td></tr>';
    } else {
      body.innerHTML = pg.items.map((r: any) => {
        const pct = r.capture_pct;
        const color = pct == null ? 'var(--text-muted)' : pct < 50 ? 'var(--danger)' : pct < 80 ? 'var(--warning)' : 'var(--success)';
        return `
          <tr>
            <td><span class="u-truncate" style="display:block; max-width:240px;" title="${Formatters.escapeHtml(r.name)}">${Formatters.escapeHtml(r.name)}</span></td>
            <td style="text-align: right;">${ProjectionsPage.money(r.basis)}</td>
            <td style="text-align: right; color: var(--text-muted);">${ProjectionsPage.money(r.market_low)} – ${ProjectionsPage.money(r.market_high)}</td>
            <td style="text-align: right; color: var(--success);">${ProjectionsPage.money(r.realized || 0)}</td>
            <td style="text-align: right; color: var(--info);">${ProjectionsPage.money(r.upcoming || 0)}</td>
            <td style="text-align: right; font-weight: 600;">${ProjectionsPage.money(r.booked)}</td>
            <td style="text-align: right; font-weight: 700; color: ${r.behind > 0 ? '#ea580c' : 'var(--success)'};">${r.behind > 0 ? ProjectionsPage.money(r.behind) : '✓ ahead'}</td>
            <td style="text-align: right; font-weight: 600; color: ${color};">${pct == null ? '—' : ProjectionsPage.pct(pct, 0)}</td>
          </tr>
        `;
      }).join('');
    }

    if (pagerEl) {
      pagerEl.innerHTML = pager({
        idPrefix: 'proj-cmp',
        page: pg.page,
        pages: pg.pages,
        total: pg.total,
        pageSize: this.cmpPageSize,
      });
    }

    this.bindCompareControls();
  }

  private bindCompareControls(): void {
    const search = document.getElementById('proj-cmp-search') as HTMLInputElement | null;
    search?.addEventListener('input', () => {
      this.cmpSearch = search.value;
      this.cmpPage = 1;
      this.renderCompareTable();
      const next = document.getElementById('proj-cmp-search') as HTMLInputElement | null;
      if (next) { next.focus(); next.setSelectionRange(next.value.length, next.value.length); }
    });
    const capture = document.getElementById('proj-cmp-capture') as HTMLSelectElement | null;
    capture?.addEventListener('change', () => {
      this.cmpCapture = capture.value;
      this.cmpPage = 1;
      this.renderCompareTable();
    });
    document.getElementById('proj-cmp-prev')?.addEventListener('click', () => { this.cmpPage--; this.renderCompareTable(); });
    document.getElementById('proj-cmp-next')?.addEventListener('click', () => { this.cmpPage++; this.renderCompareTable(); });
  }

  // Column-sort clicks — bound once on the static header row.
  private bindCompareSort(): void {
    const head = document.getElementById('proj-airroi-compare-head');
    if (!head || (head as any)._sortBound) return;
    (head as any)._sortBound = true;
    head.addEventListener('click', (e) => {
      const th = (e.target as HTMLElement).closest<HTMLElement>('[data-cmp-sort]');
      if (!th) return;
      const key = th.dataset.cmpSort!;
      if (this.cmpSortKey === key) this.cmpSortDir = this.cmpSortDir === 'desc' ? 'asc' : 'desc';
      else { this.cmpSortKey = key; this.cmpSortDir = key === 'name' ? 'asc' : 'desc'; }
      this.cmpPage = 1;
      this.renderCompareTable();
    });
  }

  // Render the embedded AirROI charts (seasonality, potential-vs-booked, biggest gaps).
  // The portfolio monthly response is cached so the Month selector can re-render
  // without re-fetching. Per-listing data is annual-only, so the Month selector
  // drives the portfolio-level pace chart + summary and highlights the chosen month.
  private async renderAirroiCharts(): Promise<void> {
    const container = document.getElementById('proj-airroi-charts-container');
    if (!container) return;

    const compare = this.airroiCompareData;
    if (this.viewType !== 'portfolio' || !compare || !compare.available || !compare.rows?.length) {
      container.style.display = 'none';
      this.airroiCharts.forEach(c => c.destroy());
      this.airroiCharts = [];
      this.airroiMonthlyData = null;
      return;
    }
    container.style.display = '';

    // 1. Seasonality / pace — booked by month vs AirROI projected line (portfolio rollup).
    // Cached so the Month selector re-renders without a re-fetch.
    this.airroiMonthlyData = await api.get<any>(`/projections/airroi/${this.currentYear}?scenario=expected`);

    this.drawAirroiCharts();
  }

  // Draw (or redraw) the AirROI charts using already-loaded data. Reads the AirROI
  // Month selector so a month change can call this directly with no network round-trip.
  private drawAirroiCharts(): void {
    const compare = this.airroiCompareData;
    if (!compare || !compare.available || !compare.rows?.length) return;

    // Tear down any previous instances to avoid leaks / ghost canvases.
    this.airroiCharts.forEach(c => c.destroy());
    this.airroiCharts = [];

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const sel = this.airroiMonth;
    const monthIdx = sel === 'all' ? -1 : parseInt(sel, 10) - 1; // -1 = full year

    // Month summary line + pace chart, driven by the portfolio monthly data.
    const summaryEl = document.getElementById('proj-airroi-month-summary');
    const monthlyResp = this.airroiMonthlyData;
    const seasonCanvas = document.getElementById('airroi-season-chart') as HTMLCanvasElement;
    if (monthlyResp?.available) {
      const bm = monthlyResp.booked_monthly || [];
      const pm = monthlyResp.monthly || [];

      if (summaryEl) {
        if (monthIdx >= 0) {
          const b = bm[monthIdx] || { realized: 0, upcoming: 0 };
          const p = pm[monthIdx] || { projected_gross: 0 };
          const booked = (b.realized || 0) + (b.upcoming || 0);
          const capture = p.projected_gross > 0 ? Math.round((booked / p.projected_gross) * 100) : null;
          summaryEl.innerHTML =
            `<strong>${monthNames[monthIdx]} ${this.currentYear}:</strong> ` +
            `booked $${Formatters.formatNumber(booked)} ` +
            `(realized $${Formatters.formatNumber(b.realized || 0)}, upcoming $${Formatters.formatNumber(b.upcoming || 0)}) ` +
            `vs. AirROI projected $${Formatters.formatNumber(p.projected_gross)}` +
            (capture == null ? '' : ` — ${capture}% of projected`) +
            ` · property breakdowns below remain full-year (AirROI per-property data is annual)`;
        } else {
          summaryEl.textContent = 'Showing full year. Pick a month to focus the pace chart and summary.';
        }
      }

      if (seasonCanvas) {
        // Highlight the selected month by dimming the others.
        const realizedBg = bm.map((_: any, i: number) => (monthIdx < 0 || i === monthIdx) ? '#16a34a' : 'rgba(22,163,74,0.25)');
        const upcomingBg = bm.map((_: any, i: number) => (monthIdx < 0 || i === monthIdx) ? '#0ea5e9' : 'rgba(14,165,233,0.25)');
        this.airroiCharts.push(new Chart(seasonCanvas, {
          data: {
            labels: monthNames,
            datasets: [
              { type: 'bar', label: 'Realized', data: bm.map((m: any) => m.realized), backgroundColor: realizedBg, stack: 'booked' },
              { type: 'bar', label: 'Upcoming', data: bm.map((m: any) => m.upcoming), backgroundColor: upcomingBg, stack: 'booked' },
              { type: 'line', label: 'Projected (AirROI)', data: pm.map((m: any) => m.projected_gross), borderColor: '#94a3b8', borderDash: [5, 4], backgroundColor: 'transparent', tension: 0.35, pointRadius: 2 }
            ]
          },
          options: { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: true, position: 'top' }, tooltip: { callbacks: { label: (c) => `${c.dataset.label}: $${Number(c.raw).toLocaleString()}` } } }, scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true, ticks: { callback: (v) => '$' + (Number(v) / 1000) + 'k' } } } }
        }));
      }
    }

    // Per-property market potential / gaps now live exclusively in the
    // sortable leakage table (renderCompareTable) — the old top-20/top-15 bar
    // charts hid everything past the cutoff and couldn't answer "who's behind".
  }

  private async loadBLRProjections(): Promise<void> {
    try {
      const data = await api.get<BLREstimate[]>('/blr-projections');
      this.blrProjections = data || [];
      this.renderBLRTable();
    } catch (error) {
      console.error('Failed to load BLR projections:', error);
    }
  }

  private async loadFeeBreakdown(): Promise<void> {
    try {
      const monthSel = document.getElementById('proj-chart-month') as HTMLSelectElement | null;
      if (!monthSel) return; // composed view may not render this control yet
      const selectedMonth = monthSel.value;
      let startMonth = 1, endMonth = 12;
      
      if (selectedMonth === 'h1') { startMonth = 1; endMonth = 6; }
      else if (selectedMonth === 'h2') { startMonth = 7; endMonth = 12; }
      else if (!isNaN(parseInt(selectedMonth))) { startMonth = endMonth = parseInt(selectedMonth); }
      
      const startDate = `${this.currentYear}-${String(startMonth).padStart(2, '0')}-01`;
      const lastDay = new Date(this.currentYear, endMonth, 0).getDate();
      const endDate = `${this.currentYear}-${String(endMonth).padStart(2, '0')}-${lastDay}`;
      
      const data = await api.get<any>(`/portfolio/revenue-breakdown?start_date=${startDate}&end_date=${endDate}`);
      if (data && data.fee_breakdown) {
        this.renderFeeBreakdown(data);
      }
    } catch (error) {
      console.error('Failed to load fee breakdown:', error);
    }
  }

  // The months (1–12) currently selected in the Chart Period dropdown.
  // Drives the chart, the stat cards, and the data table so they stay in sync.
  private selectedMonths(): number[] {
    const sel = (document.getElementById('proj-chart-month') as HTMLSelectElement)?.value || 'all';
    if (sel === 'h1') return [1, 2, 3, 4, 5, 6];
    if (sel === 'h2') return [7, 8, 9, 10, 11, 12];
    const n = parseInt(sel, 10);
    if (!isNaN(n)) return [n];
    return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
  }

  private renderStats(): void {
    const statsContainer = document.getElementById('proj-stats');
    if (!statsContainer) return;

    const months = this.selectedMonths();
    const totals = this.projectionsData.filter(row => months.includes(row.month)).reduce((acc, row) => {
      acc.projected_gross += row.projected_gross;
      acc.actual_gross += row.actual_gross;
      acc.projected_mgmt_fee += row.projected_mgmt_fee;
      acc.actual_mgmt_fee += row.actual_mgmt_fee;
      return acc;
    }, { projected_gross: 0, actual_gross: 0, projected_mgmt_fee: 0, actual_mgmt_fee: 0 });

    const selectedMonth = (document.getElementById('proj-chart-month') as HTMLSelectElement).value;
    let periodLabel = '';
    if (selectedMonth === 'h1') periodLabel = ' (H1)';
    else if (selectedMonth === 'h2') periodLabel = ' (H2)';
    else if (selectedMonth !== 'all') periodLabel = ` (${this.getMonthName(parseInt(selectedMonth))})`;

    // Each metric card is a chart selector: click GOR/CR/BLR/MOR/TOR and the
    // chart below re-plots that metric's projected vs actual series. Selected
    // metric reads as tone-good (green) — same as the old `highlight` state.
    const card = (key: string, ico: string, label: string, value: string, sub: string) => `
      <div class="kpi-card clickable proj-metric-card ${this.selectedMetric === key ? 'tone-good' : ''}" data-proj-metric="${key}" title="Chart this metric">
        <div class="kpi-card-top"><span class="kpi-card-label">${label}</span><span class="kpi-card-ic">${icon(ico, 17)}</span></div>
        <div class="kpi-card-value">${value}</div>
        <div class="kpi-card-cap">${sub}</div>
      </div>`;
    statsContainer.innerHTML =
      card('gor', 'wallet', `${this.regName('gor', 'GOR')}${periodLabel}`, ProjectionsPage.money(totals.actual_gross || totals.projected_gross), this.metricRegistry['gor']?.notes || 'gross owner revenue') +
      card('cr', 'receipt', this.regName('cr', 'CR'), ProjectionsPage.money(totals.projected_mgmt_fee), this.metricRegistry['cr']?.notes || 'commission revenue') +
      card('blr', 'sparkles', this.regName('blr', 'BLR'), ProjectionsPage.money(this.calculateTotalBLR()), this.metricRegistry['blr']?.notes || 'bottom-line fees (estimate)') +
      card('mor', 'trending', this.regName('mor', 'MOR'), ProjectionsPage.money(totals.actual_mgmt_fee || totals.projected_mgmt_fee), this.metricRegistry['mor']?.notes || 'management revenue') +
      card('tor', 'target', this.regName('tor', 'TOR'), ProjectionsPage.money((totals.actual_gross || totals.projected_gross) * (1 - this.expenseRatio / 100)), this.metricRegistry['tor']?.notes || `after ${this.expenseRatio}% expenses`) +
      `<div class="kpi-card" style="opacity:0.85;"><div class="kpi-card-top"><span class="kpi-card-label">Charting</span><span class="kpi-card-ic">${icon('compass', 17)}</span></div><div class="kpi-card-value" style="font-size:18px;">${this.metricLabel(this.selectedMetric)}</div><div class="kpi-card-cap">click a card to switch</div></div>`;

    // Delegated once on the container — survives the innerHTML rerenders.
    if (!(statsContainer as any)._metricBound) {
      (statsContainer as any)._metricBound = true;
      statsContainer.addEventListener('click', (e) => {
        const c = (e.target as HTMLElement).closest<HTMLElement>('[data-proj-metric]');
        if (!c) return;
        this.selectedMetric = c.dataset.projMetric as any;
        this.renderStats();
        this.renderChart();
      });
    }
  }

  private metricLabel(m: string): string {
    const builtins = { gor: 'GOR · Gross', cr: 'CR · Commission', blr: 'BLR · Bottom-line', mor: 'MOR · Mgmt revenue', tor: 'TOR · After expenses' } as Record<string, string>;
    const custom = this.metricRegistry[m]?.display_name;
    return custom || builtins[m] || m;
  }

  // Monthly projected/actual series for the selected metric. GOR and CR come
  // straight from stored projections; BLR is the flat fee estimate spread
  // monthly (no monthly tracking yet — labeled an estimate); TOR derives from
  // gross via the expense ratio; MOR = commission + the BLR estimate.
  private metricSeries(monthIdx: number[]): { projected: number[]; actual: number[]; name: string } {
    const blrMonthly = this.calculateTotalBLR() / 12;
    const pick = (i: number, p: 'projected' | 'actual'): number => {
      const row = this.projectionsData[i];
      if (!row) return 0;
      const gross = p === 'projected' ? row.projected_gross : row.actual_gross;
      const fee = p === 'projected' ? row.projected_mgmt_fee : row.actual_mgmt_fee;
      switch (this.selectedMetric) {
        case 'cr': return fee;
        case 'blr': return blrMonthly;
        case 'mor': return fee + blrMonthly;
        case 'tor': return gross * (1 - this.expenseRatio / 100);
        default: return gross;
      }
    };
    return {
      projected: monthIdx.map(i => Math.round(pick(i, 'projected'))),
      actual: monthIdx.map(i => Math.round(pick(i, 'actual'))),
      name: this.metricLabel(this.selectedMetric).split('·')[1].trim(),
    };
  }

  private calculateTotalBLR(): number {
    // BLR = sum of (price * take_up_rate% * reservations_per_month)
    // Simplified for now
    return this.blrProjections.reduce((sum, fee) => {
      return sum + (fee.price_per_occurrence * (fee.take_up_rate / 100) * 30); // assume ~30 reservations per month
    }, 0);
  }

  private renderChart(): void {
    const showProjected = (document.getElementById('proj-show-projected') as HTMLInputElement | null)?.checked ?? true;
    const showActual = (document.getElementById('proj-show-actual') as HTMLInputElement | null)?.checked ?? true;
    const selectedMonth = (document.getElementById('proj-chart-month') as HTMLSelectElement).value;
    
    let monthsToShow: number[] = [];
    if (selectedMonth === 'h1') monthsToShow = [0, 1, 2, 3, 4, 5];
    else if (selectedMonth === 'h2') monthsToShow = [6, 7, 8, 9, 10, 11];
    else if (!isNaN(parseInt(selectedMonth))) monthsToShow = [parseInt(selectedMonth) - 1];
    else monthsToShow = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const labels = monthsToShow.map(i => monthNames[i]);
    // Series follow the selected metric card (GOR/CR/BLR/MOR/TOR).
    const series = this.metricSeries(monthsToShow);

    const datasets: any[] = [];
    if (showProjected) {
      datasets.push({
        label: `Projected ${series.name}`,
        data: series.projected,
        backgroundColor: 'rgba(59, 130, 246, 0.8)',
        borderColor: '#3b82f6',
        borderWidth: 1
      });
    }
    if (showActual) {
      datasets.push({
        label: `Actual ${series.name}`,
        data: series.actual,
        backgroundColor: 'rgba(34, 197, 94, 0.8)',
        borderColor: '#22c55e',
        borderWidth: 1
      });
    }

    const ctx = document.createElement('canvas');
    const container = document.getElementById('proj-chart-container');
    if (container) {
      container.innerHTML = '';
      container.appendChild(ctx);
      
      if (this.chartInstance) this.chartInstance.destroy();
      this.chartInstance = new Chart(ctx, {
        type: 'bar',
        data: { labels, datasets },
        options: {
          responsive: true,
          maintainAspectRatio: true,
          plugins: {
            legend: { position: 'top' },
            tooltip: { callbacks: { label: (ctx) => `$${ctx.raw}` } }
          },
          scales: { y: { beginAtZero: true, ticks: { callback: (value) => '$' + value } } }
        }
      });
    }
  }

  private renderProjectionsTable(): void {
    const tbody = document.getElementById('proj-table-body');
    if (!tbody) return;
    
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    let totals = { projected_gross: 0, projected_mgmt_fee: 0, projected_net: 0, actual_gross: 0, actual_mgmt_fee: 0, actual_net: 0 };

    const months = this.selectedMonths();
    tbody.innerHTML = this.projectionsData.filter(row => months.includes(row.month)).map(row => {
      totals.projected_gross += row.projected_gross;
      totals.projected_mgmt_fee += row.projected_mgmt_fee;
      totals.projected_net += row.projected_net;
      totals.actual_gross += row.actual_gross;
      totals.actual_mgmt_fee += row.actual_mgmt_fee;
      totals.actual_net += row.actual_net;
      
      const variance = row.projected_gross > 0 ? ((row.actual_gross - row.projected_gross) / row.projected_gross * 100).toFixed(1) : 0;
      const varNum = parseFloat(variance as string);
      const varClass = varNum >= 0 ? 'text-pos' : 'text-neg';
      const varStr = Number.isFinite(varNum) ? `${varNum >= 0 ? '+' : ''}${variance}%` : '—';

      return `
        <tr>
          <td><strong>${monthNames[row.month - 1]}</strong></td>
          <td style="background: #eff6ff;"><input type="number" class="editable-input" data-month="${row.month}" data-field="projected_gross" value="${Math.round(row.projected_gross)}" style="width: 90px;"></td>
          <td style="background: var(--bg-subtle);">${ProjectionsPage.money(row.projected_mgmt_fee)}</td>
          <td style="background: var(--bg-subtle);">${ProjectionsPage.money(row.projected_net)}</td>
          <td style="background: #f0fdf4;"><input type="number" class="editable-input" data-month="${row.month}" data-field="actual_gross" value="${Math.round(row.actual_gross)}" style="width: 90px;"></td>
          <td style="background: var(--bg-subtle);">${ProjectionsPage.money(row.actual_mgmt_fee)}</td>
          <td style="background: var(--bg-subtle);">${ProjectionsPage.money(row.actual_net)}</td>
          <td class="${varClass}">${varStr}</td>
        </tr>
      `;
    }).join('');
    
    const totalVar = totals.projected_gross > 0 ? ((totals.actual_gross - totals.projected_gross) / totals.projected_gross * 100).toFixed(1) : 0;
    const totalVarNum = parseFloat(totalVar);
    const totalVarClass = totalVarNum >= 0 ? 'text-pos' : 'text-neg';
    const totalVarStr = Number.isFinite(totalVarNum) ? `${totalVarNum >= 0 ? '+' : ''}${totalVar}%` : '—';

    const tfoot = document.getElementById('proj-table-foot');
    if (tfoot) {
      tfoot.innerHTML = `
        <tr style="background: var(--border); font-weight: 600;">
          <td>TOTAL</td>
          <td>${ProjectionsPage.money(totals.projected_gross)}</td>
          <td>${ProjectionsPage.money(totals.projected_mgmt_fee)}</td>
          <td>${ProjectionsPage.money(totals.projected_net)}</td>
          <td>${ProjectionsPage.money(totals.actual_gross)}</td>
          <td>${ProjectionsPage.money(totals.actual_mgmt_fee)}</td>
          <td>${ProjectionsPage.money(totals.actual_net)}</td>
          <td class="${totalVarClass}">${totalVarStr}</td>
        </tr>
      `;
    }
  }

  private renderBLRTable(): void {
    const tbody = document.getElementById('blr-projections-body');
    if (!tbody) return;
    
    if (!this.blrProjections.length) {
      tbody.innerHTML = '<tr><td colspan="4" class="empty-state">No BLR fees configured. Click "+ Add BLR Fee" to add.<\/td><\/tr>';
      return;
    }
    
    tbody.innerHTML = this.blrProjections.map(fee => `
      <tr data-id="${fee.id}">
        <td><input type="text" class="blr-name" value="${Formatters.escapeHtml(fee.fee_name)}" style="width: 150px; padding: 4px;"></td>
        <td><input type="number" class="blr-price" value="${fee.price_per_occurrence}" step="0.01" style="width: 80px; padding: 4px;"></td>
        <td><input type="number" class="blr-rate" value="${fee.take_up_rate}" step="1" style="width: 60px; padding: 4px;">%</td>
        <td><button class="btn btn-sm btn-danger delete-blr" data-id="${fee.id}">Delete</button></td>
      </tr>
    `).join('');
  }

  private renderFeeBreakdown(data: any): void {
    const tbody = document.getElementById('proj-fee-breakdown-body');
    const tfoot = document.getElementById('proj-fee-breakdown-foot');
    if (!tbody) return;
    
    if (!data.fee_breakdown || Object.keys(data.fee_breakdown).length === 0) {
      tbody.innerHTML = '<tr><td colspan="5" class="empty-state">No fee data for this period.<\/td><\/tr>';
      if (tfoot) tfoot.innerHTML = '';
      return;
    }
    
    const categoryLabels: Record<string, string> = {
      'CR': '<span style="color: #3b82f6;">CR</span>',
      'BLR': '<span style="color: #22c55e;">BLR</span>',
      'Pass-through': '<span style="color: #64748b;">Pass-through</span>',
      'Excluded': '<span style="color: #dc2626;">Excluded</span>'
    };
    
    const rows = Object.entries(data.fee_breakdown).map(([name, fee]: [string, any]) => `
      <tr>
        <td>${Formatters.escapeHtml(name)}</td>
        <td>${categoryLabels[fee.category] || Formatters.escapeHtml(String(fee.category ?? ''))}</td>
        <td>${ProjectionsPage.money(fee.amount)}</td>
        <td><strong>${ProjectionsPage.money(fee.mgmt_revenue)}</strong></td>
        <td>${Formatters.escapeHtml(String(fee.treatment ?? ''))}</td>
      </tr>
    `);

    tbody.innerHTML = rows.join('');
    if (tfoot) {
      tfoot.innerHTML = `
        <tr style="background: var(--bg-subtle); font-weight: 600;">
          <td>TOTAL</td>
          <td></td>
          <td>${ProjectionsPage.money(data.gor || 0)}</td>
          <td>${ProjectionsPage.money(data.mor || 0)}</td>
          <td></td>
        </tr>
      `;
    }
  }

  private bindEvents(): void {
    document.getElementById('proj-year')?.addEventListener('change', () => this.loadData());
    document.getElementById('proj-view-type')?.addEventListener('change', () => this.loadData());
    document.getElementById('proj-chart-month')?.addEventListener('change', () => {
      this.loadFeeBreakdown();
      this.renderChart();
      this.renderStats();
      this.renderProjectionsTable();
    });
    document.getElementById('proj-show-projected')?.addEventListener('change', () => this.renderChart());
    document.getElementById('proj-show-actual')?.addEventListener('change', () => this.renderChart());
    document.getElementById('proj-save-btn')?.addEventListener('click', () => this.saveProjections());
    document.getElementById('add-blr-btn')?.addEventListener('click', () => this.addBLRFee());
    document.getElementById('proj-generate-btn')?.addEventListener('click', () => this.generateFromPriceLabs());
    document.getElementById('proj-generate-airroi-btn')?.addEventListener('click', () => this.generateFromAirROI());
    document.getElementById('proj-airroi-scenario')?.addEventListener('change', (e) => {
      this.airroiScenario = (e.target as HTMLSelectElement).value;
      // The leakage table re-bases Behind $ / Capture % on the same scenario:
      // conservative → p25, optimistic → p90, else the stored expected value.
      this.cmpBasis = this.airroiScenario === 'conservative' ? 'low'
        : this.airroiScenario === 'optimistic' ? 'high' : 'expected';
      this.renderCompareTable();
      this.loadData();
    });
    document.getElementById('proj-airroi-month')?.addEventListener('change', (e) => {
      // Data is already loaded client-side — just redraw, no re-fetch needed.
      this.airroiMonth = (e.target as HTMLSelectElement).value;
      this.drawAirroiCharts();
    });
    document.getElementById('proj-sync-btn')?.addEventListener('click', () => this.syncActuals());
    
    document.getElementById('proj-expense-ratio')?.addEventListener('change', (e) => {
      this.expenseRatio = parseFloat((e.target as HTMLInputElement).value);
      this.renderProjectionsTable();
    });
    
    this.container.addEventListener('click', (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('delete-blr')) {
        const id = parseInt(target.dataset.id || '0');
        this.deleteBLRFee(id);
      }
    });

    this.container.addEventListener('change', (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('editable-input')) {
        const month = parseInt(target.dataset.month || '0');
        const field = target.dataset.field || '';
        const value = parseFloat((target as HTMLInputElement).value) || 0;
        const row = this.projectionsData.find(r => r.month === month);
        if (row) {
          (row as any)[field] = value;
          this.renderProjectionsTable();
          this.renderChart();
        }
      }
    });
  }

  private async saveProjections(): Promise<void> {
    const btn = document.getElementById('proj-save-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Saving...';
    
    try {
      const data = this.projectionsData.map(row => ({
        month: row.month,
        year: this.currentYear,
        projected_gross: row.projected_gross,
        projected_mgmt_fee: row.projected_mgmt_fee,
        projected_net: row.projected_net,
        actual_gross: row.actual_gross,
        actual_mgmt_fee: row.actual_mgmt_fee,
        actual_net: row.actual_net
      }));
      
      await api.post('/projections/bulk', { projections: data });
      alert('Projections saved successfully!');
    } catch (error) {
      console.error('Failed to save projections:', error);
      alert('Failed to save projections');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Save All Changes';
    }
  }

  private async addBLRFee(): Promise<void> {
    const name = prompt('Fee name (e.g., "Pet Fee"):');
    if (!name) return;
    const price = parseFloat(prompt('Price per occurrence:', '0') || '0');
    const rate = parseFloat(prompt('Take-up rate %:', '50') || '50');
    
    try {
      await api.post('/blr-projections', { fee_name: name, price_per_occurrence: price, take_up_rate: rate });
      await this.loadBLRProjections();
      await this.renderStats();
    } catch (error) {
      console.error('Failed to add BLR fee:', error);
    }
  }

  private async deleteBLRFee(id: number): Promise<void> {
    if (!confirm('Delete this BLR projection?')) return;
    try {
      await api.delete(`/blr-projections?id=${id}`);
      await this.loadBLRProjections();
      await this.renderStats();
    } catch (error) {
      console.error('Failed to delete BLR fee:', error);
    }
  }

  private async generateFromAirROI(): Promise<void> {
    const btn = document.getElementById('proj-generate-airroi-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Fetching market data…';

    try {
      const res = await api.post<any>('/projections/generate-from-airroi', { year: this.currentYear });
      if (res) {
        alert(`AirROI projections generated.\n` +
          `Listings with market data: ${res.with_estimate}/${res.eligible} (skipped ${res.skipped}).\n` +
          `Annual expected: $${Formatters.formatNumber(res.annual.expected)} ` +
          `(range $${Formatters.formatNumber(res.annual.low)} – $${Formatters.formatNumber(res.annual.high)})`);
      }
      // Show the expected scenario by default after generating.
      this.airroiScenario = 'expected';
      const sel = document.getElementById('proj-airroi-scenario') as HTMLSelectElement;
      if (sel) sel.value = 'expected';
      await this.loadData();
    } catch (error) {
      console.error('Failed to generate from AirROI:', error);
      alert('Failed to generate from AirROI');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Generate from AirROI';
    }
  }

  private async generateFromPriceLabs(): Promise<void> {
    const btn = document.getElementById('proj-generate-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Generating...';
    
    try {
      await api.post('/projections/generate-from-pricelabs', { year: this.currentYear, months_ahead: 12 });
      alert('Projections generated from PriceLabs!');
      await this.loadData();
    } catch (error) {
      console.error('Failed to generate from PriceLabs:', error);
      alert('Failed to generate projections');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Generate from PriceLabs';
    }
  }

  private async syncActuals(): Promise<void> {
    const btn = document.getElementById('proj-sync-btn') as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = 'Syncing...';
    
    try {
      await api.post(`/projections/sync-actuals?year=${this.currentYear}`);
      alert('Actuals synced from Hostaway!');
      await this.loadData();
    } catch (error) {
      console.error('Failed to sync actuals:', error);
      alert('Failed to sync actuals');
    } finally {
      btn.disabled = false;
      btn.textContent = 'Sync Actuals';
    }
  }

  private getMonthName(month: number): string {
    const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return names[month - 1] || '';
  }
}
